/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useContext, useState } from "react";
import axios from "axios";
import { NavLink, withRouter } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

import context from "../../../../../../context/auth-context";
import errorImage from "../../../../../../assets/images/image-error.png";
import errorHandler from "../../../../../../hoc/ErrorHandler";
import preloader from "../../../../../../assets/images/preloader.gif";

import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Paper from "@mui/material/Paper";

const UserProfile = (props) => {
  const authContext = useContext(context);
  const dispatch = useDispatch();

  const [companyAiEnabled, setCompanyAiEnabled] = useState(false);
  const [userAiEnabled, setUserAiEnabled] = useState(false);
  const [userSettingsEnabled, setUserSettingsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState("success");

  const showMessage = (msg, type = "success") => {
    setSnackbarMessage(msg);
    setSnackbarType(type);
    setShowSnackbar(true);
    setTimeout(() => setShowSnackbar(false), 3000);
  };

  
  useEffect(() => {
    if (!authContext.authenticated) props.history.replace({ pathname: "/" });
    const getUserAvatar = async () => {
      const response = await axios.get(
        `/users/${authContext.userId}/account/avatar`,
        {
          headers: {
            "Company-Code": authContext.companyCode,
          },
          responseType: "blob",
        }
      );

      if (response) {
        response.data instanceof Blob
          ? props.onSetAvatarURI(response.data)
          : console.log("NO DATA");
      } else {
        props.onSetAvatarURIError();
      }
    };
    const getUserGroups = async () => {
      const groups = await axios.get(
        `/users/${authContext.userId}/account/groups`,
        {
          headers: {
            "Company-Code": authContext.companyCode,
            "Company-Id": authContext.companyId,
          },
        }
      );

      if (groups) {
        return props.onInstantiateGroups(groups.data);
      } else {
        return props.onInstantiateGroupsError();
      }
    };

    getUserAvatar();
    getUserGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authContext.authenticated, authContext.companyId]);

  // Fetch AI settings when modal opens
  useEffect(() => {
    if (!aiModalOpen || !authContext.accessToken) return;

    const fetchAiSettings = async () => {
      try {
        // Company AI
        const companyRes = await axios.get("/ai/usage-control", {
          headers: {
            Authorization: authContext.accessToken,
            "Company-Code": authContext.companyCode,
            "Company-Id": authContext.companyId,
          },
        });
        setCompanyAiEnabled(companyRes.data.enabled);

        // User AI
        const userRes = await axios.get("/ai/usage-control", {
          headers: {
            Authorization: authContext.accessToken,
            "Company-Code": authContext.companyCode,
            "Company-Id": authContext.companyId,
          },
          params: { user_id: authContext.userId },
        });
        setUserAiEnabled(userRes.data.enabled);
      } catch (err) {
        console.error("AI fetch error:", err);
        showMessage("Error fetching AI settings", "error");
      }
    };

    fetchAiSettings();
  }, [aiModalOpen, authContext]);

  const toggleCompanyAi = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        "/ai/usage-control",
        { enabled: !companyAiEnabled },
        {
          headers: {
            Authorization: authContext.accessToken,
            "Company-Code": authContext.companyCode,
            "Company-Id": authContext.companyId,
          },
        }
      );
      setCompanyAiEnabled(res.data.enabled);
      showMessage(`Company AI ${res.data.enabled ? "enabled" : "disabled"}`);
    } catch (err) {
      console.error(err);
      showMessage("Failed to update company AI", "error");
    }
    setLoading(false);
  };

  const toggleUserAi = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        "/ai/usage-control",
        { enabled: !userAiEnabled, user_id: authContext.userId },
        {
          headers: {
            Authorization: authContext.accessToken,
            "Company-Code": authContext.companyCode,
            "Company-Id": authContext.companyId,
          },
        }
      );
      setUserAiEnabled(res.data.enabled);
      showMessage(`User AI ${res.data.enabled ? "enabled" : "disabled"}`);
    } catch (err) {
      console.error(err);
      showMessage("Failed to update user AI", "error");
    }
    setLoading(false);
  };

  const handleUserSettingsToggle = (event) => {
    setUserSettingsEnabled(event.target.checked);
  };

  const openAiModal = () => setAiModalOpen(true);
  const closeAiModal = () => setAiModalOpen(false);

  const userSignOutHandler = () => {
    axios
      .post(`/auth/logout`, { userName: authContext.loginName })
      .then((res) => {
        dispatch({ type: "REMOVE_LOADING" });
        props.onClearGeoInfo();
        props.onClearTimeZones();
        props.onClearCompanies();
        props.onSignOut();
        props.history.replace({ pathname: "/" });
      })
      .catch(() => alert("Cannot Log Out, Please Try Again"));
  };

  const avatarSrc = !props.avatarURI.error
    ? props.avatarURI.URI ?? preloader
    : errorImage;

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 3,
    borderRadius: 1,
    outline: "none",
  };

  return (
    <>
      <li className="nav-item dropdown" id="profile-menu">
        <a className="nav-link navbar-avatar"
          data-toggle="dropdown"
          aria-expanded="false"
          data-animation="scale-up"
          role="button">
          <span
            className={
              props.avatarURI.error
                ? "avatar avatar-offline"
                : "avatar avatar-online"
            }
          >
            <img
              src={
                !props.avatarURI.error
                  ? props.avatarURI.URI ?? preloader
                  : errorImage
              }
              alt={authContext.userName}
            />
            <i></i>
          </span>
        </a>
        <div className="dropdown-menu">
          <NavLink to="/auth/profile" className="dropdown-item">
            <i className="icon ti-user"></i>&nbsp;Profile
          </NavLink>
          <div className="dropdown-divider"></div>
          <button
            className="btn btn-link dropdown-item"
            role="menuitem"
            data-toggle="modal"
            data-target="#screenLockModal"
          >
            <i className="icon ti-lock" aria-hidden="true"></i>&nbsp;Lock screen
          </button>
          <button className="btn btn-link dropdown-item" onClick={openAiModal}>
            <i className="icon ti-power-off"></i>&nbsp;AI Switch
          </button>
          <button className="btn btn-link dropdown-item" onClick={userSignOutHandler}>
            <i className="icon ti-power-off"></i>&nbsp;Logout
          </button>
        </div>
      </li>

      {/* AI Modal */}
      <Modal open={aiModalOpen} onClose={closeAiModal}>
        <Paper elevation={3} sx={modalStyle}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography variant="h6" gutterBottom>
              AI Usage Control
            </Typography>

            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Chip icon={<BusinessIcon />} label={authContext.companyName || "Company"} size="small" />
              <Chip
                icon={authContext.isAdmin ? <AdminPanelSettingsIcon /> : <PersonIcon />}
                label={authContext.userName || "User"}
                color={authContext.isAdmin ? "primary" : "default"}
                size="small"
              />
            </Box>

            {authContext.isAdmin && (
              <FormControlLabel
                control={
                  <Switch checked={userSettingsEnabled} onChange={handleUserSettingsToggle} />
                }
                label="User-specific settings"
                sx={{ mb: 2 }}
              />
            )}

            {/* Company AI Toggle */}
            <Box display="flex" alignItems="center" mb={2} sx={{ cursor: loading ? "default" : "pointer" }} onClick={!loading ? toggleCompanyAi : undefined}>
              {companyAiEnabled ? (
                <ToggleOnIcon sx={{ color: "#2e7d32", fontSize: 50 }} />
              ) : (
                <ToggleOffIcon sx={{ color: "text.disabled", fontSize: 50 }} />
              )}
              <Box ml={2}>
                <Typography fontWeight="bold">
                  {`Company AI ${companyAiEnabled ? "ON" : "OFF"}`}
                </Typography>
              </Box>
            </Box>

            {/* User AI Toggle */}
            {userSettingsEnabled && (
              <Box display="flex" alignItems="center" mb={2} sx={{ cursor: loading ? "default" : "pointer" }} onClick={!loading ? toggleUserAi : undefined}>
                {userAiEnabled ? (
                  <ToggleOnIcon sx={{ color: "#2e7d32", fontSize: 50 }} />
                ) : (
                  <ToggleOffIcon sx={{ color: "text.disabled", fontSize: 50 }} />
                )}
                <Box ml={2}>
                  <Typography fontWeight="bold">{`User AI ${userAiEnabled ? "ON" : "OFF"}`}</Typography>
                </Box>
              </Box>
            )}

            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2, width: "100%" }}>
              <Button onClick={closeAiModal} variant="contained">
                Close
              </Button>
            </Box>
          </Box>
        </Paper>
      </Modal>

      <Snackbar open={showSnackbar} autoHideDuration={3000} onClose={() => setShowSnackbar(false)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snackbarType} onClose={() => setShowSnackbar(false)} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

const mapStateToProps = (state) => ({
  avatarURI: state.auth.avatarURI,
});

const mapDispatchToProps = (dispatch) => {
  return {
    onSetAvatarURI: (URI) => dispatch({ type: "SET_AVATAR_URI", payload: URI }),
    onSetAvatarURIError: () => dispatch({ type: "SET_AVATAR_URI" }),
    onSignOut: () => dispatch({ type: "SIGN_OUT" }),
    onClearGeoInfo: () => dispatch({ type: "CLEAR_GEO_INFO" }),
    onClearTimeZones: () => dispatch({ type: "CLEAR_TIME_ZONES" }),
    onClearCompanies: () => dispatch({ type: "CLEAR_COMPANIES" }),
    onInstantiateGroups: (groups) =>
      dispatch({ type: "INSTANTIATE_USER_GROUPS", payload: groups }),
    onInstantiateGroupsError: () =>
      dispatch({ type: "INSTANTIATE_USER_GROUPS_ERROR" }),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(errorHandler(UserProfile, axios)));
